import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Loader2,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  Eye,
  EyeOff,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const DWHValidation = () => {
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    tables_in_sync: false,
    missing_snowflake: false,
    missing_schemas: false,
    differences: false,
  });
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  // Fetch validation report
  const fetchReport = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      const url = forceRefresh
        ? "/dwh/validation/report?force_refresh=true"
        : "/dwh/validation/report";
      const response = await fetchWithAuth(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setReport(data);
      setLastRefreshTime(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh report in background
  const refreshReport = async () => {
    try {
      setRefreshing(true);
      const response = await fetchWithAuth("/dwh/validation/refresh", {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Wait for background processing then fetch updated report
      setTimeout(async () => {
        try {
          await fetchReport(true);
        } finally {
          setRefreshing(false);
        }
      }, 3000);
    } catch (err) {
      setError(err.message);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString("fr-FR");
  };

  const getSeverityColor = (type) => {
    switch (type) {
      case "success":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "warning":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "error":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "info":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">
          Loading validation report...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 dark:text-red-400 p-4">
        <h3 className="font-medium mb-2">Error loading validation report</h3>
        <p className="text-sm mb-4">{error}</p>
        <Button onClick={() => fetchReport()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (!report) return null;

  const { summary, details } = report;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate("/dwh-schemas")}
            variant="outline"
            size="sm"
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 ml-2" />
            <span className="hidden sm:inline m-2">DWH Schemas</span>
          </Button>
          <div>
            <h2 className="text-2xl font-bold dark:text-white">
              DWH Schema Validation
            </h2>
            <div className="flex items-center space-x-3 mt-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Report: {formatTimestamp(report.timestamp)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {refreshing && (
            <div className="flex items-center text-sm text-blue-600 dark:text-blue-400">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Refreshing...
            </div>
          )}
          <div className="flex space-x-2">
            <Button
              onClick={refreshReport}
              variant="ghost"
              size="sm"
              disabled={refreshing}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              title="Generate fresh report in background"
            >
              <Clock className="h-4 w-4" />
              <span className="hidden md:inline ml-1">Refresh report</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="text-2xl font-bold dark:text-white">
                  {summary.tables_in_sync}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Tables in sync
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <XCircle className="h-8 w-8 text-red-600 mr-3" />
              <div>
                <p className="text-2xl font-bold dark:text-white">
                  {summary.tables_with_differences}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Tables with differences
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-yellow-600 mr-3" />
              <div>
                <p className="text-2xl font-bold dark:text-white">
                  {summary.tables_missing_in_snowflake}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Missing in Snowflake
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Database className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <p className="text-2xl font-bold dark:text-white">
                  {summary.tables_missing_in_schemas}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Missing schemas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                Snowflake Tables:
              </span>
              <span className="ml-2 font-medium dark:text-white">
                {summary.snowflake_tables_count}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">
                JSON Schemas:
              </span>
              <span className="ml-2 font-medium dark:text-white">
                {summary.json_schemas_count}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tables in Sync */}
      {details.tables_in_sync.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                Tables in Sync ({details.tables_in_sync.length})
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection("tables_in_sync")}
              >
                {expandedSections.tables_in_sync ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          {expandedSections.tables_in_sync && (
            <CardContent>
              <div className="space-y-3">
                {details.tables_in_sync.map((table, index) => (
                  <div
                    key={index}
                    className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm dark:text-white">
                          {table.table_name}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {table.description}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Created:{" "}
                          {formatTimestamp(table.snowflake_info.created)} • Last
                          modified:{" "}
                          {formatTimestamp(table.snowflake_info.last_altered)}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getSeverityColor("success")}>
                          {table.snowflake_info.row_count.toLocaleString()} rows
                        </Badge>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {table.columns_count} columns
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Tables Missing in Snowflake */}
      {details.tables_missing_in_snowflake.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                Tables Missing in Snowflake (
                {details.tables_missing_in_snowflake.length})
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection("missing_snowflake")}
              >
                {expandedSections.missing_snowflake ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          {expandedSections.missing_snowflake && (
            <CardContent>
              <div className="space-y-3">
                {details.tables_missing_in_snowflake.map((table, index) => (
                  <div
                    key={index}
                    className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg"
                  >
                    <div className="font-medium text-sm dark:text-white">
                      {table.table_name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {table.description}
                    </div>
                    <Badge className={`mt-2 ${getSeverityColor("warning")}`}>
                      Schema Defined
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Tables Missing in Schemas */}
      {details.tables_missing_in_schemas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <Database className="h-5 w-5 text-blue-600 mr-2" />
                Tables Missing in Schemas (
                {details.tables_missing_in_schemas.length})
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection("missing_schemas")}
              >
                {expandedSections.missing_schemas ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          {expandedSections.missing_schemas && (
            <CardContent>
              <div className="space-y-3">
                {details.tables_missing_in_schemas.map((table, index) => (
                  <div
                    key={index}
                    className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm dark:text-white">
                          {table.table_name}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          Created: {formatTimestamp(table.created)}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getSeverityColor("info")}>
                          {table.row_count.toLocaleString()} rows
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Tables with Differences */}
      {Object.keys(details.table_differences).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <XCircle className="h-5 w-5 text-red-600 mr-2" />
                Tables with Differences (
                {Object.keys(details.table_differences).length})
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleSection("differences")}
              >
                {expandedSections.differences ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </CardTitle>
          </CardHeader>
          {expandedSections.differences && (
            <CardContent>
              <div className="space-y-4">
                {Object.entries(details.table_differences).map(
                  ([tableName, tableDiff]) => (
                    <div
                      key={tableName}
                      className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-medium text-sm dark:text-white">
                            {tableName}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {tableDiff.snowflake_info.row_count.toLocaleString()}{" "}
                            rows • Last modified:{" "}
                            {formatTimestamp(
                              tableDiff.snowflake_info.last_altered
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Column Differences */}
                      {Object.keys(tableDiff.column_differences).length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-xs text-gray-700 dark:text-gray-300">
                            Column Differences:
                          </h4>
                          {Object.entries(tableDiff.column_differences).map(
                            ([columnName, columnDiff]) => (
                              <div
                                key={columnName}
                                className="pl-4 border-l-2 border-red-300 dark:border-red-700"
                              >
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="font-mono text-xs dark:text-white">
                                    {columnName}
                                  </span>
                                  {columnDiff.primary_key && (
                                    <Badge
                                      className={getSeverityColor("error")}
                                    >
                                      PK
                                    </Badge>
                                  )}
                                  {columnDiff.system && (
                                    <Badge className={getSeverityColor("info")}>
                                      SYS
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                                  {columnDiff.description}
                                </div>
                                <div className="space-y-1">
                                  {columnDiff.differences.map(
                                    (diff, diffIndex) => (
                                      <div key={diffIndex} className="text-xs">
                                        <span className="text-gray-500 dark:text-gray-400">
                                          {diff.property}:
                                        </span>
                                        <span className="ml-1 text-red-600 dark:text-red-400 font-mono">
                                          {JSON.stringify(diff.snowflake_value)}
                                        </span>
                                        <span className="mx-1">→</span>
                                        <span className="text-green-600 dark:text-green-400 font-mono">
                                          {JSON.stringify(diff.expected_value)}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}

                      {/* Missing Columns */}
                      {tableDiff.columns_missing_in_snowflake.length > 0 && (
                        <div className="mt-3">
                          <h4 className="font-medium text-xs text-gray-700 dark:text-gray-300 mb-2">
                            Missing in Snowflake:
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {tableDiff.columns_missing_in_snowflake.map(
                              (col, index) => (
                                <Badge
                                  key={index}
                                  className={getSeverityColor("warning")}
                                >
                                  {col.column_name}
                                </Badge>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {tableDiff.columns_missing_in_schema.length > 0 && (
                        <div className="mt-3">
                          <h4 className="font-medium text-xs text-gray-700 dark:text-gray-300 mb-2">
                            Missing in Schema:
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {tableDiff.columns_missing_in_schema.map(
                              (col, index) => (
                                <Badge
                                  key={index}
                                  className={getSeverityColor("info")}
                                >
                                  {col.column_name}
                                </Badge>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Success Message */}
      {summary.tables_in_sync > 0 &&
        Object.keys(details.table_differences).length === 0 &&
        details.tables_missing_in_snowflake.length === 0 &&
        details.tables_missing_in_schemas.length === 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  All {summary.tables_in_sync} table
                  {summary.tables_in_sync > 1 ? "s are" : " is"} perfectly
                  synchronized between Snowflake and schemas.
                </span>
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default DWHValidation;
